import { inject, InjectionToken } from '@angular/core';
import { ResolveFn, Route } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { publicOnlyGuard } from './public-only.guard';

/**
 * Injection token controlling whether the sign-up link is shown on the login page.
 * Defaults to `true` when not provided.
 *
 * Provide `false` in applications that don't support self-registration (e.g. the control UI).
 */
export const AUTH_SHOW_SIGNUP = new InjectionToken<boolean>('AUTH_SHOW_SIGNUP', {
  providedIn: 'root',
  factory: () => true,
});

/**
 * Configuration options for authentication routes
 */
export interface AuthRoutesConfig {
  /**
   * Path for the login route. Default: 'login'
   */
  loginPath?: string;

  /**
   * Path for the register route. Default: 'register'
   */
  registerPath?: string;

  /**
   * Whether to include the register route at all.
   * Set to `false` for apps that don't support self-registration.
   * Default: true
   */
  showRegister?: boolean;

  /**
   * Whether to apply the public-only guard to auth routes.
   * When true, authenticated users will be redirected away from login/register pages.
   * Default: true
   */
  usePublicOnlyGuard?: boolean;

  loginTitle?: string | ResolveFn<string>;
  registerTitle?: string | ResolveFn<string>;
}

/**
 * Provides default authentication routes (login and register)
 * that can be customized via the config parameter.
 *
 * @param config Optional configuration for customizing the auth routes
 * @returns Array of Route objects for login and register pages
 *
 * @example
 * // Use default routes
 * export const appRoutes: Route[] = [
 *   ...provideAuthRoutes(),
 *   { path: 'calendar', component: CalendarComponent, canActivate: [authGuard] }
 * ];
 *
 * @example
 * // Customize paths and titles
 * export const appRoutes: Route[] = [
 *   ...provideAuthRoutes({
 *     loginPath: 'signin',
 *     registerPath: 'signup',
 *     loginTitle: 'Sign In - MyApp',
 *     registerTitle: 'Sign Up - MyApp'
 *   }),
 *   { path: 'dashboard', component: DashboardComponent, canActivate: [authGuard] }
 * ];
 *
 * @example
 * // Disable public-only guard to allow custom behavior
 * export const appRoutes: Route[] = [
 *   ...provideAuthRoutes({ usePublicOnlyGuard: false }),
 *   { path: 'home', component: HomeComponent }
 * ];
 */
export function provideAuthRoutes(config: AuthRoutesConfig = {}): Route[] {
  const {
    loginPath = 'login',
    registerPath = 'register',
    showRegister = true,
    usePublicOnlyGuard = true,
    loginTitle = (): string => inject(TranslateService).instant('auth.routes.loginTitle'),
    registerTitle = (): string => inject(TranslateService).instant('auth.routes.registerTitle'),
  } = config;

  const guards = usePublicOnlyGuard ? [publicOnlyGuard] : [];

  const routes: Route[] = [
    {
      path: loginPath,
      loadComponent: () => import('./login.component').then(m => m.LoginComponent),
      canActivate: guards,
      title: loginTitle,
    },
  ];

  if (showRegister) {
    routes.push({
      path: registerPath,
      loadComponent: () => import('./register.component').then(m => m.RegisterComponent),
      canActivate: guards,
      title: registerTitle,
    });
  }

  return routes;
}
